import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    currencyUnit,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    KpiCard,
    MetricBasis,
    MetricBasisToggle,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

function periodLabel(since, until) {
    return `${since.format("D. M. YYYY")} - ${until.format("D. M. YYYY")}`;
}

function moneyDeltaPercent(current, previous) {
    return dividePercent(moneyAmount(current) - moneyAmount(previous), moneyAmount(previous));
}

function numberDeltaPercent(current, previous) {
    return dividePercent(Number(current ?? 0) - Number(previous ?? 0), Number(previous ?? 0));
}

function periodMetrics(period, turnoverKey, profitKey, orderKey) {
    const turnover = moneyAmount(period?.[turnoverKey]);
    const orders = Number(period?.[orderKey] ?? 0);
    const marketing = moneyAmount(period?.orderSpendMarketing);
    const profit = moneyAmount(period?.[profitKey]);

    return {
        aov: orders > 0 ? turnover / orders : null,
        pno: turnover > 0 ? marketing / turnover * 100 : null,
        margin: turnover > 0 ? profit / turnover * 100 : null,
    };
}

function comparisonTone(value) {
    if (value == null) {
        return "default";
    }

    if (value < 0) {
        return "bad";
    }

    if (value > 0) {
        return "good";
    }

    return "default";
}

export default function ReportingPeriodComparisonLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [periods, setPeriods] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [metricBasis, setMetricBasis] = useState(MetricBasis.Marketing);
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const comparisons = useMemo(() => {
        const days = until.diff(since, "days") + 1;
        const previousUntil = since.clone().subtract(1, "day");
        const previousSince = previousUntil.clone().subtract(days - 1, "days");
        const yearSince = since.clone().subtract(1, "year");
        const yearUntil = until.clone().subtract(1, "year");

        return [
            {
                id: "current",
                label: t("reporting_lab.comparison.current_period", {defaultValue: "Aktuální období"}),
                since,
                until,
            },
            {
                id: "previous",
                label: t("reporting_lab.comparison.previous_period", {defaultValue: "Předchozí období"}),
                since: previousSince,
                until: previousUntil,
            },
            {
                id: "year",
                label: t("reporting_lab.comparison.previous_year", {defaultValue: "Minulý rok"}),
                since: yearSince,
                until: yearUntil,
            },
        ];
    }, [since, until, t]);

    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setPeriods(null);
        setSnapshots([]);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.reportingPeriodComparison({
            projectId: project.id,
            periodComparisonInput: {
                current: {
                    since: comparisons[0].since.format("YYYY-MM-DD"),
                    until: comparisons[0].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
                previous: {
                    since: comparisons[1].since.format("YYYY-MM-DD"),
                    until: comparisons[1].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
                previousYear: {
                    since: comparisons[2].since.format("YYYY-MM-DD"),
                    until: comparisons[2].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            },
        })
            .then((nextPeriods) => {
                if (!active) {
                    return;
                }

                setPeriods({
                    current: nextPeriods.current,
                    previous: nextPeriods.previous,
                    year: nextPeriods.previousYear,
                });
                setSnapshots([
                    nextPeriods.currentSnapshot,
                    nextPeriods.previousSnapshot,
                    nextPeriods.previousYearSnapshot,
                ]);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, comparisons, centreFilterKey, domainFilterKey]);
    const current = periods?.current;
    const previous = periods?.previous;
    const year = periods?.year;
    const currency = current?.currency
        ?? current?.orderTurnoverGross?.currency
        ?? previous?.orderTurnoverGross?.currency
        ?? projectCurrency(project);
    const turnoverKey = metricBasis === MetricBasis.Completed ? "orderTurnoverNet" : "orderTurnoverGross";
    const profitKey = metricBasis === MetricBasis.Completed ? "orderProfitNet" : "orderProfitGross";
    const hasProfit = current?.[profitKey] != null;
    const orderKey = metricBasis === MetricBasis.Completed ? "orderAmountNet" : "orderAmountGross";
    const currentMetrics = periodMetrics(current, turnoverKey, profitKey, orderKey);
    const previousMetrics = periodMetrics(previous, turnoverKey, profitKey, orderKey);
    const yearMetrics = periodMetrics(year, turnoverKey, profitKey, orderKey);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const rows = [
        {
            label: t("reporting_lab.comparison.revenue", {defaultValue: "Obrat"}),
            current: current?.[turnoverKey],
            previous: previous?.[turnoverKey],
            year: year?.[turnoverKey],
            kind: "money",
        },
        {
            label: t("reporting_lab.comparison.profit", {defaultValue: "Marže"}),
            current: current?.[profitKey],
            previous: previous?.[profitKey],
            year: year?.[profitKey],
            kind: "money",
            sensitiveProfit: true,
        },
        {
            label: t("reporting_lab.comparison.marketing", {defaultValue: "Marketing"}),
            current: current?.orderSpendMarketing,
            previous: previous?.orderSpendMarketing,
            year: year?.orderSpendMarketing,
            kind: "money",
        },
        {
            label: t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"}),
            current: current?.[orderKey],
            previous: previous?.[orderKey],
            year: year?.[orderKey],
            kind: "number",
        },
        {
            label: t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"}),
            current: current?.orderAmountCancelled,
            previous: previous?.orderAmountCancelled,
            year: year?.orderAmountCancelled,
            kind: "number",
            negativeDirection: true,
        },
        {
            label: t("reporting_lab.table.aov", {defaultValue: "AOV"}),
            current: currentMetrics.aov,
            previous: previousMetrics.aov,
            year: yearMetrics.aov,
            kind: "currencyNumber",
        },
        {
            label: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
            current: currentMetrics.pno,
            previous: previousMetrics.pno,
            year: yearMetrics.pno,
            kind: "percent",
            negativeDirection: true,
        },
        {
            label: t("reporting_lab.comparison.margin", {defaultValue: "Marže po marketingu"}),
            current: currentMetrics.margin,
            previous: previousMetrics.margin,
            year: yearMetrics.margin,
            kind: "percent",
            sensitiveProfit: true,
        },
    ];
    const visibleRows = rows.filter((row) => hasProfit || !row.sensitiveProfit);
    const moneyRows = visibleRows.filter((row) => row.kind === "money");
    const countRows = visibleRows.filter((row) => row.kind === "number");
    const renderComparisonValue = (row, value) => {
        if (value == null) {
            return "-";
        }

        if (row.kind === "money") {
            return renderCurrencyValue(value, true);
        }

        if (row.kind === "currencyNumber") {
            return renderCurrencyValue({amount: Math.round(value), currency}, true);
        }

        if (row.kind === "percent") {
            return renderPercentValue(value);
        }

        return renderNumber(value);
    };

    const moneyChartOption = {
        color: ["#2563eb", "#16a34a", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: moneyRows.map((row) => row.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            name: currencyUnit(currency),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: comparisons.map((comparison) => ({
            name: comparison.label,
            type: "bar",
            data: moneyRows.map((row) => moneyToMajor(row[comparison.id])),
            barMaxWidth: 30,
        })),
    };

    const countChartOption = {
        color: ["#2563eb", "#16a34a", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: countRows.map((row) => row.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            name: t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"}),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: comparisons.map((comparison) => ({
            name: comparison.label,
            type: "bar",
            data: countRows.map((row) => Number(row[comparison.id] ?? 0)),
            barMaxWidth: 30,
        })),
    };

    const marginOption = {
        color: ["#16a34a", "#f97316", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: comparisons.map((comparison) => comparison.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.comparison.margin", {defaultValue: "Marže"}),
                type: "bar",
                data: [current, previous, year].map((period) => dividePercent(moneyAmount(period?.[profitKey]), moneyAmount(period?.[turnoverKey])) ?? 0),
                barMaxWidth: 34,
            },
            {
                name: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
                type: "bar",
                data: [current, previous, year].map((period) => dividePercent(moneyAmount(period?.orderSpendMarketing), moneyAmount(period?.[turnoverKey])) ?? 0),
                barMaxWidth: 34,
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.comparison.margin", {defaultValue: "Marže"})),
    };

    const revenueDelta = moneyDeltaPercent(current?.[turnoverKey], previous?.[turnoverKey]);
    const profitDelta = moneyDeltaPercent(current?.[profitKey], previous?.[profitKey]);
    const orderDelta = numberDeltaPercent(current?.[orderKey], previous?.[orderKey]);

    return <WideSecureLayout
        title={t("reporting_lab.pages.period_comparison", {defaultValue: "Srovnání období"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshots, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.period_comparison.title", {defaultValue: "K čemu slouží srovnání období"})}
                    actions={<MetricBasisToggle value={metricBasis} onChange={setMetricBasis} metricBasisMeta={metricBasisMeta}/>}
                >
                    {t("reporting_lab.descriptions.period_comparison.text", {defaultValue: "Porovnává aktuální období s předchozím stejně dlouhým obdobím a stejným datumovým oknem posunutým o rok zpět. Ukazuje změny v obratu, marži, objednávkách i marketingových nákladech."})}
                </ReportingPageDescription>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue(current?.[turnoverKey], true)} meta={`${metricBasisMeta[metricBasis]?.shortLabel} · ${renderPercentValue(revenueDelta)} ${t("reporting_lab.comparison.vs_previous", {defaultValue: "vs předchozí období"})}`} tone={comparisonTone(revenueDelta)}/>
                </div>
                {hasProfit && <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.profit", {defaultValue: "Marže"})} value={renderCurrencyValue(current?.[profitKey], true)} meta={`${metricBasisMeta[metricBasis]?.shortLabel} · ${renderPercentValue(profitDelta)} ${t("reporting_lab.comparison.vs_previous", {defaultValue: "vs předchozí období"})}`} tone={comparisonTone(profitDelta)}/>
                </div>}
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})} value={renderNumber(current?.[orderKey])} meta={`${metricBasisMeta[metricBasis]?.shortLabel} · ${renderPercentValue(orderDelta)} ${t("reporting_lab.comparison.vs_previous", {defaultValue: "vs předchozí období"})}`} tone={comparisonTone(orderDelta)}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.comparison.periods", {defaultValue: "Porovnávaná období"})} className="col-span-12">
                    <div className="grid gap-3 md:grid-cols-3">
                        {comparisons.map((comparison) => <div key={comparison.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{comparison.label}</div>
                            <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{periodLabel(comparison.since, comparison.until)}</div>
                        </div>)}
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.comparison.financial_comparison", {defaultValue: "Finanční srovnání"})} className="col-span-12 xl:col-span-8">
                    <EChart option={moneyChartOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.comparison.order_comparison", {defaultValue: "Objednávky a storna"})} className="col-span-12 xl:col-span-4">
                    <EChart option={countChartOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.comparison.efficiency_comparison", {defaultValue: "Efektivita"})} className="col-span-12">
                    <EChart option={marginOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.comparison.table", {defaultValue: "Tabulka srovnání"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.comparison.metric", {defaultValue: "Metrika"})}</th>
                                {comparisons.map((comparison) => <th key={comparison.id} className="py-2 pr-4 text-right font-semibold">{comparison.label}</th>)}
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.comparison.change", {defaultValue: "Změna"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visibleRows.map((row) => {
                                const delta = row.kind === "money"
                                    ? moneyDeltaPercent(row.current, row.previous)
                                    : numberDeltaPercent(row.current, row.previous);

                                return <tr key={row.label}>
                                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.label}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.current)}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.previous)}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.year)}</td>
                                    <td className={`py-3 text-right font-semibold ${delta == null ? "text-slate-500" : row.negativeDirection ? (delta > 0 ? "text-rose-600" : "text-emerald-600") : (delta < 0 ? "text-rose-600" : "text-emerald-600")}`}>
                                        {delta == null ? t("reporting_lab.comparison.not_comparable", {defaultValue: "Nelze porovnat"}) : renderPercentValue(delta)}
                                    </td>
                                </tr>;
                            })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
